import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./landing.css";

const HERO_SERVICES = [
  { slug: "instant-pnl", title: "Instant P&L from Bank Statements", badge: "New · AI-Powered", desc: "Audit-grade P&L in 48 hours from bank statements.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg> },
  { slug: "tax-strategy", title: "Tax Services", desc: "Federal, state, and multi-state prep and planning.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 14l6-6M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /></svg> },
  { slug: "financial-advisory", title: "Financial Advisory", desc: "Growth consulting, valuations, and GAAP reporting.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg> },
  { slug: "estate-trust-planning", title: "Wills & Trust Services", desc: "Estate plans, living trusts, succession roadmaps.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg> },
  { slug: "business-formation", title: "Business Formation", desc: "LLC, S-Corp, and C-Corp setup across all 50 states.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M15 9h.01M9 13h.01M15 13h.01M9 17h.01M15 17h.01" /></svg> },
  { slug: "payroll", title: "Payroll Management", desc: "Direct deposit, tax filings, W-2 and 1099 handling.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /><path d="M6 12h.01M18 12h.01" /></svg> },
  { slug: "notary", title: "Notary & Admin Support", desc: "Official notary services for contracts and deeds.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="9" r="4" /><path d="M12 13v8M8 21h8" /></svg> },
];

const VALID_CAT_KEYS = ["all", "tax", "advisory", "international", "ai"];

function readCatFromLocation() {
  if (typeof window === "undefined") return "all";
  // Support both formats:
  //   /?cat=ai      (URLSearchParams on location.search)
  //   /#services?cat=ai   (query string embedded in the hash)
  const params = new URLSearchParams(window.location.search);
  let cat = params.get("cat");
  if (!cat) {
    const hash = window.location.hash || "";
    const qIdx = hash.indexOf("?");
    if (qIdx !== -1) {
      const hashParams = new URLSearchParams(hash.slice(qIdx + 1));
      cat = hashParams.get("cat");
    }
  }
  return cat && VALID_CAT_KEYS.includes(cat) ? cat : "all";
}

function writeCatToLocation(cat) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!cat || cat === "all") url.searchParams.delete("cat");
  else url.searchParams.set("cat", cat);
  // Preserve any hash the user landed with (e.g. #services)
  window.history.replaceState(null, "", url.toString());
}

function ServicesGridWithFilters() {
  const [filter, setFilter] = useState(() => readCatFromLocation());
  const filtered = filter === "all" ? SERVICES : SERVICES.filter((s) => s.category === filter);
  const counts = SERVICE_CATEGORIES.reduce((acc, c) => {
    acc[c.key] = c.key === "all" ? SERVICES.length : SERVICES.filter((s) => s.category === c.key).length;
    return acc;
  }, {});
  useEffect(() => {
    writeCatToLocation(filter);
  }, [filter]);
  useEffect(() => {
    const onPop = () => setFilter(readCatFromLocation());
    window.addEventListener("popstate", onPop);
    window.addEventListener("hashchange", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener("hashchange", onPop);
    };
  }, []);
  return (
    <div className="al-services-stack-wrap" data-testid="services-grid-with-filters">
      <div className="al-service-filters" role="tablist" aria-label="Filter services by category">
        {SERVICE_CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            type="button"
            role="tab"
            aria-selected={filter === cat.key}
            className={`al-service-filter ${filter === cat.key ? "is-active" : ""}`}
            onClick={() => setFilter(cat.key)}
            data-testid={`service-filter-${cat.key}`}
          >
            <span>{cat.label}</span>
            <span className="al-service-filter-count" aria-hidden="true">{counts[cat.key]}</span>
          </button>
        ))}
      </div>
      <ServicePillStack
        key={filter}
        items={filtered}
        testIdPrefix="service"
        triggerOnScroll={true}
        layout="grid"
        staggerMs={filter === "all" ? 1000 : 100}
      />
    </div>
  );
}

function ServicePillStack({ items, testIdPrefix, label, triggerOnScroll = false, layout = "stack", staggerMs = 1000 }) {
  const [inView, setInView] = useState(!triggerOnScroll);
  const containerRef = React.useRef(null);
  useEffect(() => {
    if (!triggerOnScroll) return;
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, [triggerOnScroll]);
  const layoutClass = layout === "grid" ? "al-pill-stack--grid" : "";
  return (
    <div className={`al-pill-stack ${layoutClass} ${inView ? "in" : ""}`} ref={containerRef} data-testid={`${testIdPrefix}-stack`}>
      {label && <span className="al-pill-stack-label">{label}</span>}
      <ol className="al-pill-list" aria-label={label || "Services"}>
        {items.map((s, i) => (
          <li key={s.slug || s.title} style={{ listStyle: "none" }}>
            <Link
              to={`/services/${s.slug}`}
              className="al-pill"
              data-testid={`${testIdPrefix}-pill-${i}`}
              aria-label={s.title}
              style={{ animationDelay: `${i * staggerMs}ms` }}
            >
              <div className="al-pill-badge-wrap">
                <span className="al-pill-num">{String(i + 1).padStart(2, "0")}</span>
                {s.icon && <span className="al-pill-icon" aria-hidden="true">{s.icon}</span>}
              </div>
              <div className="al-pill-body">
                <p className="al-pill-title" title={s.title}>{s.title}</p>
                <p className="al-pill-desc" title={s.desc}>{s.desc}</p>
              </div>
              <svg className="al-pill-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

const API = process.env.REACT_APP_BACKEND_URL;

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);

const PortalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M14 12H3" /></svg>
);

const SERVICES = [
  { slug: "tax-strategy", category: "tax", title: "Tax Strategy & Compliance", desc: "Legal reduction strategies before year-end.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /></svg> },
  { slug: "fractional-cfo", category: "advisory", title: "Fractional CFO", desc: "Forecasting, KPIs, and capital planning on demand.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17" /><polyline points="16 7 22 7 22 13" /></svg> },
  { slug: "real-estate-tax", category: "tax", title: "Real Estate Tax", desc: "1031 exchanges, cost segregation, passive losses.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg> },
  { slug: "estate-trust-planning", category: "advisory", title: "Estate & Trust Planning", desc: "Trust structures, gifting, and succession roadmaps.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg> },
  { slug: "cloud-accounting", category: "advisory", title: "Cloud Accounting Setup", desc: "QuickBooks Online, Gusto, and Bill.com integrated.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2" /><path d="M8 21h8M12 17v4" /></svg> },
  { slug: "business-advisory", category: "advisory", title: "Business Advisory", desc: "Entity structure to exit — strategic financial counsel.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3" /><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg> },
  { slug: "non-resident-tax", category: "international", title: "Non-Resident & International Tax", badge: "Specialist", desc: "FBAR, FATCA, Form 5472, ITIN, treaty analysis.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg> },
  { slug: "instant-pnl", category: "ai", title: "Instant P&L from Bank Statements", badge: "New", desc: "PDF or CSV in, clean P&L out in 48 hours.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg> },
  { slug: "ai-finance", category: "ai", title: "AI Finance Implementation", badge: "New", desc: "AI agents for AR, AP, reconciliation, treasury.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /><circle cx="18" cy="6" r="3" fill="currentColor" stroke="none" opacity="0.3" /><circle cx="18" cy="6" r="1.5" /></svg> },
];

const SERVICE_CATEGORIES = [
  { key: "all", label: "All" },
  { key: "tax", label: "Tax" },
  { key: "advisory", label: "Advisory" },
  { key: "international", label: "International" },
  { key: "ai", label: "AI" },
];

const AUDIENCE = [
  { num: "01", title: "High-Net-Worth Individuals", desc: "Complex income structures and investment portfolios — managed with the discretion and depth you deserve.", items: ["Tax-loss harvesting", "Multi-state returns", "Trust administration", "Charitable giving structures"] },
  { num: "02", title: "Non-Residents & Expats", desc: "Foreign nationals and global entrepreneurs navigating US tax obligations need precision — not guesswork or missed filings.", items: ["FBAR & FATCA filings", "Treaty-based returns", "ITIN applications", "Form 5472 compliance"] },
  { num: "03", title: "Growing Businesses", desc: "Scaling from $1M to $10M+ brings new tax complexity and capital decisions. We provide the financial leadership to grow with confidence.", items: ["Entity restructuring", "R&D tax credits", "Pre-transaction planning", "AI workflow automation"] },
];

const TRUST_BADGES = [
  { label: "AICPA Member", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" /></svg> },
  { label: "QB ProAdvisor", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg> },
  { label: "FBAR / FATCA Specialist", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg> },
];

const PILLARS = [
  { title: "Proactive, not reactive", desc: "Quarterly strategy sessions — not just calls when something goes wrong. Our clients rarely face unexpected tax bills.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> },
  { title: "Domestic & global expertise", desc: "From Frisco to foreign shores — US domestic, international, and cross-border tax compliance handled in one firm.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" /></svg> },
  { title: "AI-powered financial infrastructure", desc: "We don't just advise on AI — we implement it. AR, AP, reconciliations, and treasury workflows that run with minimal human intervention.", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M9 9h6M9 12h6M9 15h4" /></svg> },
];

function ContactForm() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", service: "", message: "", honeypot: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    if (!form.first_name.trim() || !form.email.trim()) { setState("invalid"); return; }
    setState("sending");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source_page: "landing" }),
      });
      if (!res.ok) throw new Error("failed");
      setState("sent");
    } catch {
      setState("error");
    }
  };

  const btnLabel = {
    idle: "Request My Strategy Call →",
    invalid: "Please fill in your name and email",
    sending: "Sending…",
    sent: "Request sent — we'll be in touch within 1 business day.",
    error: "Something went wrong — please try again",
  }[state];

  return (
    <form className="al-cta-form" onSubmit={submit} noValidate data-testid="landing-contact-form">
      <input type="text" name="_honey" value={form.honeypot} onChange={set("honeypot")} style={{ display: "none" }} tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <div className="al-form-row">
        <div className="al-form-field">
          <label htmlFor="first-name">First name *</label>
          <input type="text" id="first-name" data-testid="contact-first-name" value={form.first_name} onChange={set("first_name")} placeholder="Jane" required autoComplete="given-name" />
        </div>
        <div className="al-form-field">
          <label htmlFor="last-name">Last name *</label>
          <input type="text" id="last-name" data-testid="contact-last-name" value={form.last_name} onChange={set("last_name")} placeholder="Smith" required autoComplete="family-name" />
        </div>
      </div>
      <div className="al-form-row">
        <div className="al-form-field">
          <label htmlFor="email">Email address *</label>
          <input type="email" id="email" data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="jane@company.com" required autoComplete="email" />
        </div>
        <div className="al-form-field">
          <label htmlFor="phone">Phone number</label>
          <input type="tel" id="phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} placeholder="(469) 000-0000" autoComplete="tel" />
        </div>
      </div>
      <div className="al-form-field">
        <label htmlFor="service">Service of interest</label>
        <select id="service" data-testid="contact-service" value={form.service} onChange={set("service")}>
          <option value="">Select a service…</option>
          <option value="Tax Strategy & Compliance">Tax Strategy &amp; Compliance</option>
          <option value="Non-Resident & International Tax (FBAR/FATCA)">Non-Resident &amp; International Tax (FBAR/FATCA)</option>
          <option value="Instant P&L from Bank Statements">Instant P&amp;L from Bank Statements</option>
          <option value="AI Finance Implementation">AI Finance Implementation</option>
          <option value="Fractional CFO">Fractional CFO</option>
          <option value="Real Estate Tax">Real Estate Tax</option>
          <option value="Estate & Trust Planning">Estate &amp; Trust Planning</option>
          <option value="Other / Not sure yet">Other / Not sure yet</option>
        </select>
      </div>
      <div className="al-form-field">
        <label htmlFor="message">Tell us about your situation</label>
        <textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} placeholder="Brief overview of your tax situation, business, or what you're looking to accomplish…" />
      </div>
      <button type="submit" className={`al-form-submit${state === "sent" ? " sent" : ""}`} disabled={state === "sending" || state === "sent"} data-testid="contact-submit-btn">
        {btnLabel}
      </button>
      <p className="al-form-privacy" aria-live="polite">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
        Your information is encrypted and never sold or shared.
      </p>
    </form>
  );
}

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="artha-landing" data-testid="landing-page">
      <nav className={`al-nav${scrolled ? " scrolled" : ""}`} role="navigation" aria-label="Main navigation">
        <div className="al-container">
          <div className="al-nav-inner">
            <a href="/" className="al-nav-logo" aria-label="Artha Business Solutions — home">
              <img src="/artha-logo.png" alt="Artha" className="al-nav-logo-mark-img" />
              <div className="al-nav-brand">
                Artha Business Solutions LLC
                <small>Tax Advisory &amp; Preparation</small>
              </div>
            </a>
            <ul className="al-nav-links">
              <li><a href="#services">Services</a></li>
              <li><Link to="/blog" data-testid="nav-blog-link">Blog</Link></li>
              <li><Link to="/deadlines" data-testid="nav-deadlines-link">Deadlines</Link></li>
              <li><a href="#about">About</a></li>
              <li><a href="#contact">Contact</a></li>
            </ul>
            <div className="al-nav-actions">
              <a href="tel:4698153491" className="al-btn al-btn-secondary" style={{ fontSize: 12, padding: "8px 16px" }}>(469) 815-3491</a>
              <Link to="/login" className="al-btn al-btn-secondary" data-testid="nav-client-portal-btn"><PortalIcon /> Client Portal</Link>
              <a href="#contact" className="al-btn al-btn-primary" data-testid="nav-schedule-call-btn">Schedule a Call</a>
            </div>
            <button className="al-nav-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Open navigation menu" aria-expanded={menuOpen} data-testid="nav-mobile-toggle">
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      <div className={`al-mobile-menu${menuOpen ? " open" : ""}`} role="dialog" aria-label="Mobile navigation" data-testid="mobile-menu">
        <a href="#services" onClick={closeMenu}>Services</a>
        <Link to="/blog" onClick={closeMenu}>Blog</Link>
        <Link to="/deadlines" onClick={closeMenu}>Deadlines</Link>
        <a href="#about" onClick={closeMenu}>About</a>
        <a href="#contact" onClick={closeMenu}>Contact</a>
        <Link to="/login" onClick={closeMenu} data-testid="mobile-client-portal-link">Client Portal</Link>
        <a href="#contact" className="al-btn al-btn-primary" onClick={closeMenu}>Schedule a Strategy Call</a>
      </div>

      <main>
        <section className="al-hero" aria-label="Hero">
          <div className="al-container">
            <div className="al-hero-inner">
              <div className="al-hero-left">
                <span className="al-eyebrow">Tax Advisory &amp; Preparation · Frisco, Texas · Serving Dallas–Fort Worth &amp; Clients Worldwide</span>
                <h1>Frisco, TX Tax Advisory Firm Helping Growth-Focused Businesses Pay Less Tax and Keep More.</h1>
                <p>Proactive tax planning and preparation, fractional CFO services, international tax compliance (FBAR, FATCA, Form 5472), AI-powered accounting automation, and cloud bookkeeping — built for founders, real estate investors, non-residents, and high-net-worth individuals across the US and globally.</p>
                <div className="al-hero-btns">
                  <a href="#contact" className="al-btn al-btn-primary" data-testid="hero-strategy-btn">Schedule a Strategy Session</a>
                  <a href="#services" className="al-btn al-btn-secondary" data-testid="hero-services-btn">View Services</a>
                </div>
              </div>
              <ServicePillStack items={HERO_SERVICES} testIdPrefix="hero-service" label="What we do" triggerOnScroll={false} />
            </div>
          </div>
        </section>

        <div className="al-trust" role="complementary" aria-label="Credentials and partnerships">
          <div className="al-container">
            <p className="al-trust-label">Trusted credentials &amp; partnerships</p>
            <div className="al-trust-badges">
              {TRUST_BADGES.map((b) => (
                <div className="al-trust-badge" key={b.label}>{b.icon}<span>{b.label}</span></div>
              ))}
            </div>
          </div>
        </div>

        <section className="al-services" id="services" aria-label="Services">
          <div className="al-container">
            <div className="al-section-header">
              <span className="al-eyebrow">Our Services</span>
              <h2>Services That Move Your Business Forward</h2>
              <p>From domestic tax strategy to international compliance, instant financial reporting, and AI-powered automation — all under one roof.</p>
            </div>
            <ServicesGridWithFilters />
          </div>
        </section>

        <section className="al-audience" id="who-we-serve" aria-label="Who we serve">
          <div className="al-container">
            <div className="al-section-header">
              <span className="al-eyebrow">Who We Serve</span>
              <h2>Our clients aren't looking for a tax preparer. They need a financial partner.</h2>
              <p>We work with clients who have outgrown reactive, once-a-year accounting — and are ready for proactive strategy.</p>
            </div>
            <div className="al-audience-grid">
              {AUDIENCE.map((a) => (
                <div className="al-audience-col" key={a.num}>
                  <span className="al-audience-num">{a.num}</span>
                  <h3>{a.title}</h3>
                  <p>{a.desc}</p>
                  <ul className="al-audience-items">
                    {a.items.map((item) => (
                      <li className="al-audience-item" key={item}><Check /> {item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="al-split" id="about" aria-label="Why Artha">
          <div className="al-split-left">
            <span className="al-eyebrow">Our Philosophy</span>
            <div className="al-split-rule" aria-hidden="true" />
            <blockquote>"Your accountant should know about your deal before your attorney does."</blockquote>
            <p>Most firms react after the year ends. We build strategies before decisions are made — because that's the only time financial planning actually creates value.</p>
          </div>
          <div className="al-split-right">
            <span className="al-eyebrow">Why clients choose Artha</span>
            <h3>Three principles that define how we work:</h3>
            <div className="al-pillars">
              {PILLARS.map((p) => (
                <div className="al-pillar" key={p.title}>
                  <div className="al-pillar-icon" aria-hidden="true">{p.icon}</div>
                  <div className="al-pillar-body">
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="al-seo" aria-label="About our Frisco Texas CPA services">
          <div className="al-container">
            <h2>Your Trusted Tax Advisory Firm in Frisco, Texas — Tax Preparation, Accounting &amp; Advisory for Dallas–Fort Worth and Beyond</h2>
            <div className="al-seo-cols">
              <p>Artha Business Solutions LLC is a full-service <strong>tax advisory firm in Frisco, TX</strong> providing tax preparation, tax planning, small business accounting, bookkeeping, payroll support, and IRS representation to individuals and businesses across <strong>Frisco, Plano, McKinney, Prosper, Little Elm, Allen, Dallas, and the greater DFW metroplex</strong>. Whether you need an experienced <strong>tax accountant near you</strong> for an individual Form 1040, a business tax return for your S-Corporation or partnership, or quarterly estimated tax planning, our licensed Certified Public Accountants build proactive strategies that legally reduce what you owe.</p>
              <p>We are one of the few DFW firms specializing in <strong>international and non-resident taxation</strong> — FBAR filing (FinCEN 114), FATCA compliance (Form 8938), Form 5472 for foreign-owned US LLCs, ITIN applications, W-8/W-9 determinations, and tax-treaty analysis for expats, foreign nationals, H-1B and L-1 visa holders, and cross-border founders. Our <strong>fractional CFO services</strong>, QuickBooks Online cloud accounting setup, <strong>instant P&amp;L from bank statements</strong> (48-hour turnaround), and AI-powered finance automation give growing businesses enterprise-level financial leadership without the full-time cost. Call <a href="tel:4698153491">(469) 815-3491</a> or request a free strategy call today.</p>
            </div>
          </div>
        </section>

        <section className="al-cta-section" id="contact" aria-label="Contact and lead capture">
          <div className="al-container">
            <div className="al-cta-inner">
              <span className="al-eyebrow">Take the First Step</span>
              <h2>Stop Leaving Money on the Table. Let's Build Your Financial Strategy.</h2>
              <p className="al-cta-sub">Schedule a no-pressure, 30-minute strategy call. We'll review your current situation and tell you exactly where we can add value — or where you're already on track.</p>
              <div className="al-calendly-row" data-testid="calendly-book-row">
                <a href="https://calendly.com/my-cpa/30" target="_blank" rel="noreferrer" className="al-calendly-btn" data-testid="calendly-book-btn">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" width="16" height="16"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  Book instantly on Calendly — pick your time now
                </a>
                <span className="al-calendly-or"><span /> or send us your details and we'll reach out <span /></span>
              </div>
              <ContactForm />
            </div>
          </div>
        </section>
      </main>

      <footer className="al-footer" role="contentinfo">
        <div className="al-container">
          <div className="al-footer-grid">
            <div>
              <a href="/" className="al-footer-logo" aria-label="Artha Business Solutions — home">
                <img src="/artha-logo.png" alt="Artha" className="al-footer-logo-mark-img" />
                <span className="al-footer-brand-name">Artha Business Solutions LLC</span>
              </a>
              <address className="al-footer-bio" style={{ fontStyle: "normal" }}>
                Proactive financial strategy for growth-focused clients. Licensed CPAs serving founders, investors, and families globally.<br /><br />
                12800 Westridge Blvd, Suite 271<br />
                Frisco, Texas 75035<br />
                <a href="tel:4698153491">(469) 815-3491</a><br />
                <a href="mailto:cpa@arthataxes.com">cpa@arthataxes.com</a>
              </address>
            </div>
            <div>
              <div className="al-footer-col-title">Services</div>
              <ul className="al-footer-links">
                <li><a href="#services">Tax Strategy</a></li>
                <li><a href="#services">Fractional CFO</a></li>
                <li><a href="#services">Non-Resident Tax</a></li>
                <li><a href="#services">P&amp;L from Bank Statements</a></li>
                <li><a href="#services">AI Implementation</a></li>
                <li><a href="#services">Estate Planning</a></li>
              </ul>
            </div>
            <div>
              <div className="al-footer-col-title">Firm</div>
              <ul className="al-footer-links">
                <li><a href="#about">About Us</a></li>
                <li><a href="#who-we-serve">Who We Serve</a></li>
                <li><a href="#contact">Contact</a></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <Link to="/login" className="al-footer-portal" data-testid="footer-client-portal-btn">
                <PortalIcon />
                Access Client Portal
              </Link>
              <div className="al-footer-contact">
                <a href="tel:4698153491">(469) 815-3491</a><br />
                <a href="mailto:cpa@arthataxes.com">cpa@arthataxes.com</a><br />
                Mon – Fri, 9 AM – 6 PM CT
              </div>
            </div>
          </div>
          <div className="al-footer-bottom">
            <p className="al-footer-copy">© 2025 Artha Business Solutions LLC. All rights reserved. &nbsp;·&nbsp; Content on this site does not constitute legal or financial advice.</p>
            <nav className="al-footer-legal" aria-label="Legal links">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/disclaimer">Disclaimer</Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
